import { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";

const renderArticleBody = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("### ")) {
      return (
        <h3 key={i} className="font-['Cutive'] text-lg font-bold text-black mt-6 mb-2">
          {trimmed.slice(4)}
        </h3>
      );
    }
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="font-['Cutive'] text-xl font-bold text-black mt-8 mb-3 pb-1 border-b-2 border-[#FFE440] inline-block">
          {trimmed.slice(3)}
        </h2>
      );
    }
    if (trimmed.startsWith("# ")) {
      return (
        <h1 key={i} className="font-['Cutive'] text-2xl font-bold text-black mt-8 mb-3">
          {trimmed.slice(2)}
        </h1>
      );
    }
    if (trimmed === "") {
      return <div key={i} className="h-2" />;
    }
    return (
      <p key={i} className="font-['Ubuntu'] text-sm leading-relaxed text-black/70 mb-3">
        {trimmed}
      </p>
    );
  });
};

export const BlogTeaser = () => {
  const { t, lang } = useLanguage();
  const [selected, setSelected] = useState(null);
  const { data: posts = [] } = useQuery({
    queryKey: ["blog"],
    queryFn: async () => (await api.get("/blog")).data,
  });

  return (
    <section id="blog" className="py-20 lg:py-32 bg-[#FAFAF7]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 max-w-xl"
        >
          <p className="font-['Ubuntu'] text-xs uppercase tracking-[0.25em] font-semibold text-[#9B5DE5] mb-3">{t.blog.eyebrow}</p>
          <h2 className="font-['Cutive'] text-2xl sm:text-3xl lg:text-4xl font-bold">{t.blog.title}</h2>
          <p className="font-['Ubuntu'] text-black/60 mt-3">{t.blog.subtitle}</p>
        </motion.div>

        {posts.length === 0 ? (
          <p className="font-['Ubuntu'] text-black/50" data-testid="blog-empty">{t.blog.empty}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {posts.slice(0, 3).map((post, i) => (
              <motion.button
                key={post.id}
                type="button"
                onClick={() => setSelected(post)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -6 }}
                className="group border-2 border-black rounded-2xl overflow-hidden bg-white text-left cursor-pointer"
                data-testid={`blog-card-${i}`}
              >
                <div className="h-44 overflow-hidden border-b-2 border-black bg-black/5">
                  {post.cover_image && (
                    <img
                      src={post.cover_image}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-5">
                  <span className="font-['Ubuntu'] text-xs uppercase tracking-wide font-bold text-[#9B5DE5]">{post.category}</span>
                  <h3 className="font-['Ubuntu'] text-lg font-bold mt-2 mb-2">{lang === "fr" ? post.title_fr : post.title_pt}</h3>
                  <p className="font-['Ubuntu'] text-sm text-black/60 line-clamp-2">
                    {lang === "fr" ? post.excerpt_fr : post.excerpt_pt}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-4 text-sm font-bold font-['Ubuntu']">
                    {t.cta.readMore} <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent
          className="max-w-3xl w-[94vw] sm:w-full max-h-[88vh] p-0 gap-0 flex flex-col overflow-hidden"
          data-testid="blog-article-dialog"
        >
          {selected && (
            <>
              <div className="shrink-0 border-b-2 border-black/10 bg-white px-6 sm:px-8 pt-6 pb-4">
                <DialogHeader className="text-left space-y-2">
                  <span className="font-['Ubuntu'] text-xs uppercase tracking-wide font-bold text-[#9B5DE5]">{selected.category}</span>
                  <DialogTitle className="font-['Cutive'] text-2xl sm:text-3xl leading-tight font-bold pr-6">
                    {lang === "fr" ? selected.title_fr : selected.title_pt}
                  </DialogTitle>
                  <DialogDescription className="font-['Ubuntu'] text-sm text-black/60 italic">
                    {lang === "fr" ? selected.excerpt_fr : selected.excerpt_pt}
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
                <div className="h-56 rounded-xl overflow-hidden border-2 border-black bg-black/5 mb-6">
                  {selected.cover_image && (
                    <img src={selected.cover_image} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div data-testid="blog-article-content">
                  {renderArticleBody(lang === "fr" ? selected.content_fr : selected.content_pt)}
                </div>
              </div>

              <div className="shrink-0 border-t-2 border-black/10 bg-white px-6 sm:px-8 py-4 flex justify-end">
                <DialogClose asChild>
                  <button
                    type="button"
                    data-testid="blog-article-close-button"
                    className="font-['Ubuntu'] font-bold text-sm border-2 border-black rounded-full px-5 py-2 hover:bg-black hover:text-white transition-colors"
                  >
                    {t.cta.close}
                  </button>
                </DialogClose>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
